/**
 * Composant de classement en direct (mode Live)
 * 
 * Affiche la liste des joueurs connectés au tournoi live avec leurs classements,
 * triable par classement tournoi, donne en cours ou général.
 * 
 * Fonctionnalités :
 * - Colonnes adaptées par vue (Tournoi: pts, Donne: état+pts, Général: rien)
 * - Ex-aequo : même rang pour les joueurs à égalité
 * - Animation CSS (translateY) lors des changements de position
 * - Mode focus : suivi automatique de la position de l'utilisateur
 * - Indicateur circulaire de progression pour les plis en cours
 */

import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import ClickablePseudo from './ClickablePseudo';

// ============================================
// Types
// ============================================

export interface LivePlayerInfo {
  pseudo: string;
  etat: string;         // "Pli n°16" ou score comme "384", ou "-"
  clDonne: string;      // Classement de la donne en cours ("-" si pas encore classé)
  pointsTournoi: string;
  clTournoi: string;    // Classement tournoi ("-" si pas classé)
  clGeneral: string;    // Classement général ("-" si pas classé)
}

export type ClassementSortBy = 'clTournoi' | 'clGeneral' | 'clDonne';

interface LiveClassementProps {
  players: LivePlayerInfo[];
  sortBy: ClassementSortBy;
  onSortChange: (sort: ClassementSortBy) => void;
  myPseudo: string;
  tablePlayerCount?: number;  // Nombre de joueurs à la table (3, 4, ou 5) pour le calcul des plis
}

// ============================================
// Constantes
// ============================================

const ROW_HEIGHT = 36; // hauteur en px d'une ligne du classement

const SORT_OPTIONS: { key: ClassementSortBy; label: string }[] = [
  { key: 'clTournoi', label: 'Tournoi' },
  { key: 'clDonne', label: 'Donne' },
  { key: 'clGeneral', label: 'Général' },
];

// ============================================
// Helpers pour le nombre total de plis
// ============================================

/**
 * Calcule le nombre total de plis en fonction du nombre de joueurs.
 * Formule du legacy : (78 - nbCartesChien) / nbJoueurs
 * - 5 joueurs : chien = 3 cartes → 75/5 = 15 plis
 * - 4 joueurs : chien = 6 cartes → 72/4 = 18 plis
 * - 3 joueurs : chien = 6 cartes → 72/3 = 24 plis
 */
function getTotalTricks(playerCount: number): number {
  const chienCards = playerCount === 5 ? 3 : 6;
  return (78 - chienCards) / playerCount;
}

// ============================================
// Helpers pour parser l'état du joueur
// ============================================

/**
 * Parse l'état d'un joueur pour l'affichage et le tri.
 * Porte la logique de retraite_clDonne_live du client legacy.
 * 
 * Tri pour le classement "Donne" :
 *   1. Joueurs avec un rang clDonne numérique → trié par rang (plus petit = meilleur)
 *   2. Joueurs terminés sans rang encore attribué (score numérique, clDonne="-")
 *      → triés par score décroissant (plus haut score = meilleur)
 *   3. Joueurs en cours de jeu (Pli n°X) sans rang → triés par avancement (pli plus élevé d'abord)
 *   4. Joueurs sans données → tout en bas
 *
 * Plages de sortKey :
 *   [1..9999]    → joueurs avec rang officiel (clDonne numérique)
 *   [10000..20000] → joueurs terminés, triés par score (20000 - score)
 *   [30000..31000] → joueurs en cours de jeu (30000 + totalPlis - pliNum)
 *   99999        → pas de données
 */
export function parseEtat(etat: string, clDonne: string): {
  display: string;
  sortKey: number;
  trickNum?: number;
} {
  if (!etat || etat === '-') {
    return { display: '-', sortKey: 99999 };
  }

  // Le joueur a-t-il un classement de donne effectif ?
  const hasRank = clDonne !== '-' && clDonne !== '' && !isNaN(parseInt(clDonne, 10));

  // Essayer de matcher "Pli n°XX" ou "Pli nXX"
  const pliMatch = etat.match(/^Pli\s*n[°o]?\s*(\d+)/i);
  if (pliMatch) {
    const pliNum = parseInt(pliMatch[1], 10);
    return {
      display: pliNum === 0 ? 'Début' : `Pli ${pliNum}`,
      // Joueurs en cours de jeu : classés APRÈS les joueurs terminés
      // Plus le pli est élevé, plus le joueur est avancé → meilleur classement
      sortKey: hasRank ? parseInt(clDonne, 10) : (30000 + (1000 - pliNum)),
      trickNum: pliNum,
    };
  }

  // Sinon, c'est probablement un score numérique (fin de donne)
  const numVal = parseInt(etat, 10);
  if (!isNaN(numVal)) {
    return {
      display: `${numVal} pts`,
      // Joueurs terminés mais pas encore classés officiellement :
      // Plus le score est élevé, meilleur est le classement
      // Les scores au tarot vont d'environ -500 à +500, on normalise dans la plage [10000..20000]
      sortKey: hasRank ? parseInt(clDonne, 10) : (15000 - numVal),
    };
  }

  return { display: etat, sortKey: 99999 };
}

/** Joueur qui a terminé la donne en cours (score ou rang, plus de pli). */
export function isLivePlayerFinished(player: LivePlayerInfo): boolean {
  const parsed = parseEtat(player.etat, player.clDonne);
  return parsed.trickNum === undefined && parsed.sortKey < 30000;
}

/** Le joueur a déjà un classement live (au moins une donne terminée). */
export function hasLiveTournamentScore(player: LivePlayerInfo): boolean {
  const ranked = player.clTournoi !== '-' && player.clTournoi !== '' && !Number.isNaN(parseInt(player.clTournoi, 10));
  if (ranked) return true;
  const pts = player.pointsTournoi?.trim() ?? '';
  return pts !== '' && pts !== '-' && pts !== '0';
}

// ============================================
// Helpers de tri et classement
// ============================================

/** Obtient la clé de tri pour un critère donné */
function getSortValue(player: LivePlayerInfo, sortBy: ClassementSortBy): number {
  switch (sortBy) {
    case 'clTournoi': {
      const v = parseInt(player.clTournoi, 10);
      return isNaN(v) ? 99999 : v;
    }
    case 'clGeneral': {
      const v = parseInt(player.clGeneral, 10);
      return isNaN(v) ? 99999 : v;
    }
    case 'clDonne': {
      return parseEtat(player.etat, player.clDonne).sortKey;
    }
  }
}

/**
 * Calcule les rangs avec gestion des ex-aequo.
 * Joueurs ayant la même valeur de tri reçoivent le même rang.
 * Les joueurs non classés (sortValue >= 99999) ou encore en jeu
 * (sortValue >= 30000, i.e. "Pli n°X") reçoivent un rang null.
 */
function computeRanks(
  sortedPlayers: LivePlayerInfo[],
  sortBy: ClassementSortBy,
): (number | null)[] {
  const ranks: (number | null)[] = [];
  for (let i = 0; i < sortedPlayers.length; i++) {
    const sortVal = getSortValue(sortedPlayers[i], sortBy);

    // Joueurs encore en jeu (Pli n°X → sortKey 30000+) ou sans données → pas de rang
    if (sortVal >= 30000) {
      ranks.push(null);
      continue;
    }

    if (i === 0) {
      ranks.push(1);
    } else {
      const prevSortVal = getSortValue(sortedPlayers[i - 1], sortBy);
      if (sortVal === prevSortVal) {
        ranks.push(ranks[i - 1]); // même rang (ex-aequo)
      } else {
        ranks.push(i + 1);
      }
    }
  }
  return ranks;
}

// ============================================
// Composant : Indicateur de progression circulaire
// ============================================

function TrickProgress({ trickNum, totalTricks }: { trickNum: number; totalTricks: number }) {
  const size = 18;
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(trickNum / totalTricks, 1);
  const offset = circumference * (1 - progress);
  const isComplete = progress >= 1;

  return (
    <svg
      className="trick-progress-ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={`Pli ${trickNum}/${totalTricks}`}
    >
      {/* Fond du cercle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={strokeWidth}
      />
      {/* Arc de progression */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={isComplete ? '#4caf50' : '#64b5f6'}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
      />
    </svg>
  );
}

// ============================================
// Composant principal
// ============================================

export default function LiveClassement({
  players,
  sortBy,
  onSortChange,
  myPseudo,
  tablePlayerCount = 4,
}: LiveClassementProps) {
  const [focusMode, setFocusMode] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const myRowRef = useRef<HTMLDivElement>(null);

  const totalTricks = useMemo(() => getTotalTricks(tablePlayerCount), [tablePlayerCount]);
  const myPseudoLower = myPseudo.toLowerCase();
  const myPlayer = useMemo(
    () => players.find(p => p.pseudo.toLowerCase() === myPseudoLower),
    [players, myPseudoLower],
  );
  const iAmRanked = Boolean(myPlayer && hasLiveTournamentScore(myPlayer));

  // Trier les joueurs
  const sortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => {
      const va = getSortValue(a, sortBy);
      const vb = getSortValue(b, sortBy);
      if (va !== vb) return va - vb;
      // En cas d'égalité, trier alphabétiquement
      return a.pseudo.localeCompare(b.pseudo);
    });
  }, [players, sortBy]);

  // Rangs avec ex-aequo
  const ranks = useMemo(() => computeRanks(sortedPlayers, sortBy), [sortedPlayers, sortBy]);

  // Map pseudo → index trié (pour le positionnement animé)
  const positionMap = useMemo(() => {
    const map = new Map<string, number>();
    sortedPlayers.forEach((p, i) => map.set(p.pseudo, i));
    return map;
  }, [sortedPlayers]);

  // Map pseudo → rang
  const rankMap = useMemo(() => {
    const map = new Map<string, number | null>();
    sortedPlayers.forEach((p, i) => map.set(p.pseudo, ranks[i]));
    return map;
  }, [sortedPlayers, ranks]);

  // Colonnes à afficher selon la vue
  const showEtat = sortBy === 'clDonne';
  const showPoints = sortBy === 'clTournoi';

  // Focus mode : scroller vers la position de l'utilisateur
  const scrollToMe = useCallback(() => {
    if (myRowRef.current && listRef.current) {
      const listRect = listRef.current.getBoundingClientRect();
      const rowRect = myRowRef.current.getBoundingClientRect();
      // Vérifier si la ligne est visible
      if (rowRect.top < listRect.top || rowRect.bottom > listRect.bottom) {
        myRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  useEffect(() => {
    // Pas d'auto-scroll tant que le joueur n'a aucun point / classement live :
    // on reste en haut de la liste (il peut scroller lui-même).
    if (focusMode && iAmRanked) {
      const timer = setTimeout(scrollToMe, 600);
      return () => clearTimeout(timer);
    }
  }, [focusMode, iAmRanked, positionMap, scrollToMe]);

  // Rendu stable : on trie les joueurs par pseudo pour la stabilité des clés React,
  // puis on utilise transform pour les positionner visuellement à leur rang trié.
  const stablePlayers = useMemo(() => {
    return [...players].sort((a, b) => a.pseudo.localeCompare(b.pseudo));
  }, [players]);

  return (
    <div className="live-classement">
      {/* Onglets de tri */}
      <div className="classement-sort-tabs">
        {SORT_OPTIONS.map(opt => (
          <button
            key={opt.key}
            className={`sort-tab ${sortBy === opt.key ? 'active' : ''}`}
            onClick={() => onSortChange(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* En-tête du tableau */}
      <div className="classement-header">
        <span className="col-rank">#</span>
        <span className="col-player">Joueur</span>
        {showEtat && <span className="col-etat">Pts</span>}
        {showPoints && <span className="col-points">Pts T.</span>}
      </div>

      {/* Liste des joueurs avec positions animées */}
      <div className="classement-list" ref={listRef}>
        {players.length === 0 ? (
          <div className="classement-empty">Aucun joueur</div>
        ) : (
          <div
            className="classement-list-inner"
            style={{ height: players.length * ROW_HEIGHT, position: 'relative' }}
          >
            {stablePlayers.map(player => {
              const sortedIndex = positionMap.get(player.pseudo) ?? 0;
              const rank = rankMap.get(player.pseudo) ?? null;
              const parsed = parseEtat(player.etat, player.clDonne);
              const isMe = player.pseudo.toLowerCase() === myPseudoLower;

              return (
                <div
                  key={player.pseudo}
                  ref={isMe ? myRowRef : undefined}
                  className={`classement-row ${isMe ? 'is-me' : ''}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: ROW_HEIGHT,
                    transform: `translateY(${sortedIndex * ROW_HEIGHT}px)`,
                    transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    willChange: 'transform',
                  }}
                >
                  {/* Rang */}
                  <span className="col-rank">
                    {rank === null ? (
                      <span className="rank-none">-</span>
                    ) : rank <= 3 ? (
                      <span className={`rank-medal rank-${rank}`}>{rank}</span>
                    ) : (
                      rank
                    )}
                  </span>

                  {/* Joueur */}
                  <div className="col-player">
                    <PlayerAvatar pseudo={player.pseudo} size={24} />
                    <ClickablePseudo pseudo={player.pseudo} className="player-pseudo">{player.pseudo}</ClickablePseudo>
                  </div>

                  {/* État (uniquement en vue Donne) */}
                  {showEtat && (
                    <span className="col-etat" title={`${player.etat} (${player.clDonne})`}>
                      <span className="etat-content">
                        {parsed.trickNum !== undefined && (
                          <TrickProgress
                            trickNum={parsed.trickNum}
                            totalTricks={totalTricks}
                          />
                        )}
                        <span className="etat-text">{parsed.display}</span>
                      </span>
                    </span>
                  )}

                  {/* Points tournoi (Tournoi et Donne) */}
                  {showPoints && (
                    <span className="col-points">
                      {player.pointsTournoi !== '0' ? player.pointsTournoi : '-'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pied : compteur + bouton focus */}
      <div className="classement-footer">
        <span className="classement-count">
          {players.length} joueur{players.length > 1 ? 's' : ''}
        </span>
        <button
          className={`focus-toggle ${focusMode ? 'active' : ''}`}
          onClick={() => setFocusMode(f => !f)}
          title={focusMode ? 'Suivi auto activé — cliquer pour désactiver' : 'Suivi auto désactivé — cliquer pour activer'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 11l19-9-9 19-2-8-8-2z" fill={focusMode ? 'currentColor' : 'none'} opacity={focusMode ? 0.25 : 1} />
          </svg>
        </button>
      </div>
    </div>
  );
}
